import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'

type ButtonVariant = 'primary' | 'secondary'
type ButtonTone = 'light' | 'dark'
type ButtonSize = 'md' | 'lg'

interface ButtonLinkProps {
    href: string
    label: string
    // Inline editing attribute for the label when it comes from the CMS
    labelCms?: string
    variant?: ButtonVariant
    tone?: ButtonTone
    size?: ButtonSize
    className?: string
    onClick?: () => void
}

const isExternal = (href: string): boolean => /^(https?:|mailto:|tel:)/i.test(href)

const surfaceStyles: Record<ButtonVariant, Record<ButtonTone, string>> = {
    primary: {
        light: 'bg-ink text-bone hover:bg-ember-deep',
        dark: 'bg-bone text-ink hover:bg-ember-bright'
    },
    secondary: {
        light: 'border border-ink/25 text-ink hover:border-ink hover:bg-ink hover:text-bone',
        dark: 'border border-bone/35 text-bone hover:border-bone hover:bg-bone hover:text-ink'
    }
}

const circleStyles: Record<ButtonTone, string> = {
    light: 'bg-ember text-paper group-hover:bg-bone group-hover:text-ink',
    dark: 'bg-ember text-paper group-hover:bg-ink group-hover:text-bone'
}

const sizeStyles: Record<ButtonVariant, Record<ButtonSize, string>> = {
    primary: {
        md: 'h-12 gap-4 pl-6 pr-1.5 text-[0.9375rem]',
        lg: 'h-14 gap-5 pl-7 pr-2 text-base'
    },
    secondary: {
        md: 'h-12 gap-2.5 px-6 text-[0.9375rem]',
        lg: 'h-14 gap-3 px-7 text-base'
    }
}

// Pill-shaped call to action. Primary carries the ember arrow, secondary is an outline.
export const ButtonLink: FC<ButtonLinkProps> = ({
    href,
    label,
    labelCms,
    variant = 'primary',
    tone = 'light',
    size = 'md',
    className,
    onClick
}): ReactNode => {
    const classes = cn(
        'group inline-flex shrink-0 select-none items-center justify-between rounded-full font-medium tracking-[-0.005em] transition-colors duration-300 ease-brand',
        surfaceStyles[variant][tone],
        sizeStyles[variant][size],
        className
    )

    const content = variant === 'primary'
        ? (
            <>
                <span data-mdlfy-cms={labelCms}>{label}</span>

                <span
                    aria-hidden='true'
                    className={cn(
                        'flex items-center justify-center rounded-full transition-[background-color,color,transform] duration-500 ease-brand group-hover:rotate-45',
                        size === 'lg' ? 'size-10' : 'size-9',
                        circleStyles[tone]
                    )}
                >
                    <ArrowUpRight className='size-4' strokeWidth={2.25} />
                </span>
            </>
        )
        : (
            <>
                <span data-mdlfy-cms={labelCms}>{label}</span>
                <ArrowUpRight aria-hidden='true' className='size-4 transition-transform duration-500 ease-brand group-hover:rotate-45' strokeWidth={2} />
            </>
        )

    if (isExternal(href)) {
        const opensNewTab = /^https?:/i.test(href)

        return (
            <a
                href={href}
                className={classes}
                onClick={onClick}
                {...(opensNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            >
                {content}
            </a>
        )
    }

    return (
        <Link href={href} className={classes} onClick={onClick}>
            {content}
        </Link>
    )
}
